import { toast } from '@tamagui/toast/v2';
import { parseISO } from 'date-fns';
import { Stack, useRouter } from 'expo-router';
import Head from 'expo-router/head';
import { useState } from 'react';
import { YStack } from 'tamagui';

import ReservationListItem from '@/components/TicketListItem';
import TicketListItemSkeleton from '@/components/TicketListItemSkeleton';
import PageScrollView from '@/components/ui/PageScrollView';
import PSButton from '@/components/ui/PSButton';
import PSLinkButton from '@/components/ui/PSLinkButton';
import PSText from '@/components/ui/PSText';
import type { MinimalBooking } from '@/src/api/bookingsApi';
import {
  useFetchReservations,
  type FetchReservationsState,
} from '@/src/hooks/useFetchReservations';
import { useSessionUser } from '@/src/lib/sessionStore';
import { downloadTickets } from '@/src/lib/ticketsPdf';
import { PSHeaderBackButtonOnTransparentHeader } from '@/components/PSHeaderButtons';

/**
 * Step one of deleting an account: what you're about to lose, and the one thing
 * you should take with you.
 *
 * The bookings are the reason this screen exists. They SURVIVE the deletion —
 * the user can still turn up — but `/bookings/{id}` is behind the token, so the
 * moment the account is gone there is no way left to open them. Hence the list,
 * the reminder that the same tickets are in their email, and the PDF.
 *
 * It warns, it doesn't block: the download is offered, never required, and a
 * failed reservations fetch doesn't stand between the user and Continue.
 */
export default function DeleteAccountReview() {
  const user = useSessionUser();
  const router = useRouter();
  const reservations = useFetchReservations();
  const [isDownloading, setIsDownloading] = useState(false);

  const upcoming =
    reservations.status === 'success'
      ? toUpcoming(reservations.reservations)
      : [];

  const handleDownload = async () => {
    setIsDownloading(true);
    try {
      await downloadTickets(upcoming, user?.fullName ?? '');
    } catch (caught) {
      // Not an API failure — nothing was fetched. The print/share stack can fail
      // for reasons none of the ApiError kinds describe, so this says its own.

      toast.error("Couldn't prepare your tickets. Please try again.");
    }
    setIsDownloading(false);
  };

  return (
    <>
      <Head>
        <title>Delete account · Putting Scene</title>
      </Head>

      <Stack.Screen
        options={{
          headerLeft: () => <PSHeaderBackButtonOnTransparentHeader />,
          unstable_headerLeftItems: () => [
            {
              type: 'custom',
              element: <PSHeaderBackButtonOnTransparentHeader />,
              hidesSharedBackground: true,
            },
          ],
        }}
      />
      <PageScrollView headerTransparent={false}>
        <YStack gap="$xl">
          <YStack gap="$sm">
            <PSText.Title>Delete your account</PSText.Title>
            <PSText.Body2 color="$textDarkMuted">
              Your profile and everything the app remembers about you goes for
              good. There is no undo, and no way to restore it later.
            </PSText.Body2>
          </YStack>

          <UpcomingBookings
            state={reservations}
            upcoming={upcoming}
            email={user?.email ?? null}
            isDownloading={isDownloading}
            onDownload={handleDownload}
          />

          <PSButton
            variant="destructive"
            title="Continue"
            onPress={() => router.push('/delete-account/confirm')}
          />
        </YStack>
      </PageScrollView>
    </>
  );
}

/** Still worth keeping a ticket for — judged on the slot's END, so an event
 *  that started an hour ago is one the user can still walk into. */
const isUpcoming = (reservation: MinimalBooking) =>
  parseISO(reservation.slot.endDatetime).getTime() > Date.now();

const toUpcoming = (reservations: MinimalBooking[]) =>
  reservations
    .filter(isUpcoming)
    // Soonest first. The ISO strings are uniform, so this sorts chronologically
    // without parsing them again.
    .sort((a, b) => a.slot.startDatetime.localeCompare(b.slot.startDatetime));

/**
 * The four things this section can be: loading, failed, nothing to say, or the
 * list plus its download.
 *
 * A failed fetch prints one line and offers a retry rather than an error page —
 * it's an aside on this screen, not its subject, and the user can carry on
 * deleting without it.
 */
function UpcomingBookings({
  state,
  upcoming,
  email,
  isDownloading,
  onDownload,
}: {
  state: FetchReservationsState;
  upcoming: MinimalBooking[];
  email: string | null;
  isDownloading: boolean;
  onDownload: () => void;
}) {
  if (state.status === 'loading') return <TicketListItemSkeleton count={2} />;

  if (state.status === 'error') {
    return (
      <YStack gap="$sm">
        <PSText.Body3 color="$textDarkMuted">
          We couldn&apos;t check whether you have bookings coming up.
        </PSText.Body3>
        <PSLinkButton title="Try again" onPress={state.retry} />
      </YStack>
    );
  }

  if (upcoming.length === 0) return null;

  return (
    <YStack gap="$md">
      <YStack gap="$sm">
        <PSText.H1>
          You have {upcoming.length} booking
          {upcoming.length === 1 ? '' : 's'} coming up
        </PSText.H1>
        <PSText.Body2 color="$textDarkMuted">
          These stay valid — you can still go. But once your account is gone you
          won&apos;t be able to open them here, so download a copy.{' '}
          {email
            ? `They're also in the email we sent to ${email}.`
            : "They're also in the email we sent you when you booked."}
        </PSText.Body2>
      </YStack>

      {upcoming.map((reservation) => (
        <ReservationListItem
          key={reservation.id}
          imageUrl={reservation.event.images[0]?.url}
          title={reservation.event.title}
          slotDatetime={reservation.slot.startDatetime}
          ticketCount={reservation.tickets.reduce(
            (sum, t) => sum + t.quantity,
            0
          )}
          venueName={reservation.event.venue.name}
          googleMapsUrl={reservation.event.venue.googleMapsUrl}
        />
      ))}

      <PSButton
        variant="outline"
        title="Download all tickets"
        loading={isDownloading}
        loadingTitle="Preparing"
        onPress={onDownload}
      />
    </YStack>
  );
}
